import React, { useState, useCallback, Fragment } from 'react'

import moment from 'moment'
import { camelCase } from 'lodash'
import withTranslate from 'react-redux-multilingual/lib/withTranslate'
import icons from '../../../../../../icons'

import { Info } from '@material-ui/icons'
import { TextField, Typography, Chip, withStyles, Grid, IconButton } from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import AssignmentIcon from '@material-ui/icons/Assignment'

import MTable from '../../../../../UI/Table/Table'
import Toggle from '../../../../../UI/Toggle/Toggle'
import Collapse from '../../../../../UI/Collapse/Collapse'
import DetailPanel from './DetailPanel/DetailPanel'
import ActionPlansModal from './ActionPlansModal/ActionPlansModal'
import { Autocomplete } from '@material-ui/lab'
import PerformanceLevelModal from './PerformanceLevelModal/PerformanceLevelModal'

function StructureTable ({
  appraisalInfo,
  inputChanged,
  groupInputChanged,
  structureData,
  // competencyTypes,
  translate,
  openCommentModal,
  calculateButtonClicked,
  maxScore,
  isAdmin
}) {
  const [modalOpen, setModalOpen] = useState(false)
  const [modalActionOpen, setModalActionOpen] = useState(false)
  const [targetRow, setTargetRow] = useState(null)
  const [currentCollapse, setCurrentCollapse] = useState([])
  const [resultItemId, setResultItemId] = useState(null)

  const isCorporate = appraisalInfo.appraisal_type?.value_list_code === 'APT01'
  const structureCode = structureData?.structure?.structure_type?.value_list_code || 'STT01'
  const isThresholdScore = !!structureData.structure.is_threshold && structureCode === 'STT02'
  const selectedAssessorId = structureData.selected_assessor_id
  const selectedAssessorScore =
    structureData.assessor_score?.find(ass => +ass.assessor_id === +selectedAssessorId) || null
  const isInputStage = !!structureData?.current_stage?.is_input_data
  const structureScoreOptions = structureData?.competency_score || []
  const commentRequireThreshold = structureData?.structure?.comment_score || []

  const tableStructures = {
    STT01: [
      {
        field: 'perspective_name',
        title: translate('perspective'),
        width: 100,
        editable: 'never',
        render: rowData => rowData?.item?.perspective?.perspective_name
      },
      {
        field: 'item_name',
        title: translate('kpiName'),
        editable: 'never',
        width: 200,
        render: rowData => {
          const isResponsible = rowData.isResponsible
          const shouldHilight = isResponsible && isCorporate
          return (
            <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography
                variant={
                  'body2'
                }>{`${rowData?.item?.item_code}: ${rowData?.item?.item_name}`}</Typography>
              <StyledChip
                style={{ marginLeft: 5 }}
                icon={<Info />}
                variant={'outlined'}
                size={'small'}
                color={shouldHilight ? 'action' : 'primary'}
                onClick={() => openModalHandler(rowData, structureCode)}
              />
              <StyledChip
                style={{ marginLeft: 5 }}
                icon={<AssignmentIcon />}
                variant={'outlined'}
                size={'small'}
                color={'default'}
                onClick={() => openModalActionHandler(rowData, structureCode)}
              />
            </span>
          )
        }
      },
      {
        field: 'frequency_type_name',
        title: translate('frequency'),
        editable: 'never',
        render: rowData => translate(camelCase(rowData?.frequency_type?.value_list_name))
      },
      {
        field: 'target_value',
        title: translate('targetValue'),
        type: 'numeric',
        editable: 'never'
      },
      {
        field: 'uom_name',
        title: translate('uom'),
        type: 'string',
        editable: 'never',
        render: rowData => rowData?.uom?.uom_name || rowData?.item?.uom?.uom_name
      },
      {
        field: 'actual_value',
        title: translate('actualValue'),
        type: 'numeric',
        editable: 'never'
      },
      {
        field: 'score',
        title: translate('score'),
        type: 'numeric',
        editable: 'never'
      },
      {
        field: 'weight_percent',
        title: translate('weightPercent'),
        type: 'numeric',
        editable: 'never'
      },
      {
        field: 'weight_score',
        title: translate('weightScore'),
        type: 'numeric',
        editable: 'never'
      },
      {
        field: 'is_waive',
        title: translate('waive'),
        render: rowData => {
          return (
            <Toggle
              disabled={!isAdmin}
              inputValue={!!rowData.is_waive}
              changed={(e, value) => {
                inputChanged({ ...rowData, is_waive: value ? 1 : 0 })
              }}
            />
          )
        },
        width: 100
      }
    ],
    STT02: [
      {
        field: 'item_code',
        title: translate('competencyCode'),
        editable: 'never',
        width: 125,
        render: rowData => rowData.item?.item_code
      },
      {
        field: 'item_name',
        title: translate('competencyName'),
        editable: 'never',
        width: 300,
        render: rowData => {
          return (
            <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant={'body2'}>{rowData?.item?.item_name}</Typography>
              <StyledChip
                style={{ marginLeft: 5 }}
                icon={<Info />}
                variant={'outlined'}
                size={'small'}
                color={'primary'}
                onClick={() => openModalHandler(rowData, structureCode)}
              />
            </span>
          )
        }
      },
      {
        field: 'target_value',
        title: translate('expectation'),
        type: 'numeric',
        editable: 'never',
        render: rowData => {
          const targetScore =
            rowData.result_competency.find(result => +result.assessor_id === +selectedAssessorId)
              ?.target_value || ''
          const scoreLabel =
            structureScoreOptions.find(item => +item.score === +targetScore)?.score_name || null

          if (isThresholdScore) {
            return scoreLabel
          }
          return targetScore
        }
      },
      {
        field: 'score',
        title: translate('score'),
        editable: 'never',
        type: 'numeric',
        width: 150,
        render: rowData => {
          let resultCompetency =
            rowData.result_competency.find(result => +result.assessor_id === +selectedAssessorId) ||
            null
          let isError = false
          let helperText = ''
          const isCompetencyInput = !!resultCompetency?.is_input_data

          if (isThresholdScore) {
            const score =
              structureScoreOptions.find(
                item =>
                  +item.cs_id === +resultCompetency?.cs_id ||
                  +item.score === +resultCompetency?.score
              ) || null

            isError = score?.score > maxScore
            helperText = isError ? translate('scoreIsHigherThanTarget') : ''

            const changeHandler = (e, value) =>
              inputChanged({
                ...rowData,
                result_competency: rowData.result_competency.map(item => {
                  if (+item.assessor_id === +selectedAssessorId) {
                    return {
                      ...item,
                      score: +value?.score || '',
                      cs_id: +value?.cs_id || null
                    }
                  }
                  return item
                })
              })

            const getOptionLabel = option => option.score_name || ''

            return (
              <Autocomplete
                value={score}
                onChange={changeHandler}
                options={structureScoreOptions}
                getOptionLabel={getOptionLabel}
                disabled={!isInputStage || !resultCompetency || !isCompetencyInput}
                renderInput={params => (
                  <TextField {...params} fullWidth error={isError} helperText={helperText} />
                )}
              />
            )
          } else {
            const inputProps = { max: maxScore || '', min: 0, style: { textAlign: 'right' } }
            const rawValue = parseFloat(resultCompetency?.score)
            const score = isNaN(rawValue) ? '' : rawValue

            isError = score > maxScore
            helperText = isError ? translate('scoreIsHigherThanTarget') : ''

            return (
              <TextField
                value={score}
                type={'number'}
                disabled={!isInputStage || !resultCompetency || !isCompetencyInput}
                inputProps={inputProps}
                error={isError}
                fullWidth
                helperText={helperText}
                onChange={e =>
                  inputChanged({
                    ...rowData,
                    result_competency: rowData.result_competency.map(item => {
                      if (+item.assessor_id === +selectedAssessorId) {
                        return { ...item, score: e.target.value || '' }
                      }
                      return item
                    })
                  })
                }
                onBlur={() =>
                  inputChanged({
                    ...rowData,
                    result_competency: rowData.result_competency.map(item => {
                      if (+item.assessor_id === +selectedAssessorId) {
                        const value = parseFloat(resultCompetency?.score)
                        return {
                          ...item,
                          score: isNaN(value) ? '' : value.toFixed(2)
                        }
                      }
                      return item
                    })
                  })
                }
              />
            )
          }
        }
      },
      {
        field: 'comment',
        title: translate('comment'),
        editable: 'never',
        render: rowData => {
          let value =
            rowData.result_competency.find(result => +result.assessor_id === +selectedAssessorId) ||
            null
          const isCompetencyInput = !!value?.is_input_data
          const required = isInputStage && commentRequireThreshold.includes(+value?.actual_value)
          const disabled = !isInputStage || !value || !required || !isCompetencyInput
          const data = value?.comment || ''
          const isError = required && !disabled && data.length < 150
          return (
            <TextField
              value={data}
              disabled={disabled}
              required={required}
              error={!!isError}
              helperText={!disabled && isError ? translate('commentIsRequiredMin150') : ''}
              onChange={e =>
                inputChanged({
                  ...rowData,
                  result_competency: rowData.result_competency.map(item => {
                    if (+item.assessor_id === +selectedAssessorId) {
                      return { ...item, comment: e.target.value }
                    }
                    return item
                  })
                })
              }
            />
          )
        }
      },
      {
        field: 'weight_percent',
        title: translate('weightPercent'),
        type: 'numeric',
        editable: 'never',
        render: rowData =>
          rowData.result_competency.find(result => +result.assessor_id === +selectedAssessorId)
            ?.weight_percent || ''
      },
      {
        field: 'weight_score',
        title: translate('weightScore'),
        type: 'numeric',
        editable: 'never',
        render: rowData =>
          rowData.result_competency.find(result => +result.assessor_id === +selectedAssessorId)
            ?.weight_score || ''
      }
    ],
    STT03: [
      {
        field: 'item_name',
        title: translate('kpiName'),
        editable: 'never',
        width: 250,
        render: rowData => rowData.item?.item_name
      },
      {
        field: 'max_value',
        title: translate('maxValue'),
        type: 'numeric',
        width: 100
      },
      {
        field: 'actual_value',
        title: translate('actualValue'),
        width: 150,
        type: 'numeric',
        editable: 'never',
        render: rowData => {
          let value = `${rowData.actual_value}`
          return (
            <TextField
              inputProps={{ style: { textAlign: 'right' } }}
              value={isNaN(+value) ? '' : value}
              type={'number'}
              disabled={!isInputStage || !value}
              onChange={e => inputChanged({ ...rowData, actual_value: e.target.value })}
              onBlur={() =>
                inputChanged({ ...rowData, actual_value: parseFloat(value || 0).toFixed(2) })
              }
            />
          )
        }
      },
      {
        field: 'over_value',
        title: translate('overValue'),
        type: 'numeric',
        editable: 'never',
        render: rowData => {
          let overValue = 0
          let max = +rowData.max_value
          let actual = +rowData.actual_value
          if (actual) {
            if (max - actual < 0) {
              overValue = Math.abs(max - actual)
            }
          }
          return overValue
        }
      },
      {
        field: 'unit_deduct_score',
        title: translate('deductScoreUnit'),
        type: 'numeric'
      },
      {
        field: 'value_get_zero',
        title: translate('valueGetZero'),
        type: 'numeric'
      },
      {
        field: 'weight_score',
        title: translate('weightScore'),
        type: 'numeric',
        editable: 'never',
        render: rowData => {
          let overValue = 0
          const max = +rowData.max_value
          const actual = +rowData.actual_value
          const deductPerUnit = +rowData.unit_deduct_score
          if (actual) {
            if (max - actual < 0) {
              overValue = Math.abs(max - actual)
            }
          }
          return overValue * -deductPerUnit
        }
      }
    ],
    STT05: [
      {
        field: 'item_code',
        title: translate('competencyCode'),
        editable: 'never',
        render: rowData => rowData.item?.item_code
      },
      {
        field: 'item_name',
        title: translate('competencyName'),
        editable: 'never',
        width: 400,
        render: rowData => {
          return (
            <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant={'body2'}>{rowData?.item?.item_name}</Typography>
              <StyledChip
                style={{ marginLeft: 5 }}
                icon={<Info />}
                variant={'outlined'}
                size={'small'}
                color={'primary'}
                onClick={() => openModalHandler(rowData, structureCode)}
              />
            </span>
          )
        }
      },
      {
        field: 'target_value',
        title: translate('expectation'),
        type: 'numeric',
        editable: 'never',
        render: rowData =>
          rowData.result_competency.find(result => +result.assessor_id === +selectedAssessorId)
            ?.target_value || ''
      },
      {
        field: 'score',
        title: translate('score'),
        editable: 'never',
        type: 'numeric',
        width: 100,
        render: rowData => {
          let value =
            rowData.result_competency.find(result => +result.assessor_id === +selectedAssessorId) ||
            null
          const isCompetencyInput = !!value?.is_input_data
          const maxScore = rowData.item.competency_max || 0
          let score = isNaN(+value?.actual_value) ? '' : value?.actual_value
          let isError = null
          let helperText = null

          if (score > maxScore) {
            isError = true
            helperText = translate('valueIsHigherThanCompetencyMax')
            score = ''
          } else if ((score * 10) % 2 !== 0) {
            isError = true
            helperText = translate('precisionIsNotEven')
            score = ''
          }

          return (
            <TextField
              value={score}
              type={'number'}
              disabled={!isInputStage || !value || !isCompetencyInput}
              inputProps={{ max: maxScore || '', step: 0.2, style: { textAlign: 'right' } }}
              error={isError}
              fullWidth
              helperText={helperText}
              onChange={e =>
                groupInputChanged({
                  ...rowData,
                  result_competency: rowData.result_competency.map(item => {
                    if (+item.assessor_id === +selectedAssessorId) {
                      return { ...item, actual_value: e.target.value }
                    }
                    return item
                  })
                })
              }
              onBlur={() =>
                groupInputChanged({
                  ...rowData,
                  result_competency: rowData.result_competency.map(item => {
                    if (+item.assessor_id === +selectedAssessorId) {
                      return {
                        ...item,
                        actual_value: parseFloat(value?.actual_value || 0).toFixed(2)
                      }
                    }
                    return item
                  })
                })
              }
            />
          )
        }
      },
      {
        field: 'comment',
        title: translate('comment'),
        editable: 'never',
        width: 200,
        render: rowData => {
          let value =
            rowData.result_competency.find(result => +result.assessor_id === +selectedAssessorId) ||
            null
          const expectation = parseFloat(value?.target_value) || 0
          const actualValue = parseFloat(value?.actual_value) || 0

          const required = isInputStage && value && actualValue >= expectation
          const isError = required && !value?.comment
          const isCompetencyInput = !!value?.is_input_data
          return (
            <TextField
              value={value?.comment || ''}
              disabled={!isInputStage || !value || !required || !isCompetencyInput}
              required={required}
              error={isError}
              helperText={
                isError ? translate('commentRequiredWhenScoreEqualOrOverExpectation') : ''
              }
              onChange={e =>
                groupInputChanged({
                  ...rowData,
                  result_competency: rowData.result_competency.map(item => {
                    if (+item.assessor_id === +selectedAssessorId) {
                      return { ...item, comment: e.target.value }
                    }
                    return item
                  })
                })
              }
            />
          )
        }
      },
      {
        field: 'weight_percent',
        title: translate('weightPercent'),
        type: 'numeric',
        editable: 'never',
        render: rowData =>
          rowData.result_competency.find(result => +result.assessor_id === +selectedAssessorId)
            ?.weight_percent || ''
      },
      {
        field: 'weight_score',
        title: translate('weightScore'),
        type: 'numeric',
        editable: 'never',
        render: rowData =>
          rowData.result_competency.find(result => +result.assessor_id === +selectedAssessorId)
            ?.weight_score || ''
      }
    ]
  }

  let rowActions = [
    {
      icon: () => <FontAwesomeIcon icon={icons.faCalculator} />,
      tooltip: translate('calculate'),
      onClick: () => calculateButtonClicked(),
      isFreeAction: true
    }
  ]

  const openModalHandler = useCallback(dataRow => {
    setModalOpen(true)
    setTargetRow(dataRow)
  }, [])

  const closeModalHandler = useCallback(() => {
    setModalOpen(false)
    setTargetRow(null)
  }, [])

  const collapseChangeHandler = useCallback(groupCode => {
    setCurrentCollapse(prevState => {
      if (prevState.includes(groupCode)) {
        return prevState.filter(item => item !== groupCode)
      } else {
        return [...prevState, groupCode]
      }
    })
  }, [])

  const openModalActionHandler = rowData => {
    setModalActionOpen(true)
    setResultItemId(rowData?.result_item_id)
  }

  const closeModalActionHandler = () => {
    setModalActionOpen(false)
    setResultItemId(null)
  }

  const structureWeight = parseFloat(structureData.weight_percent || 0).toFixed(2)
  const structWeightDisplay = `${translate('structureWeight')}: ${structureWeight} %`

  const assignDate = moment(structureData.assigned_date)
  const endDate = moment(structureData.end_date)
  const dateDisplay = (
    <Typography variant={'body2'}>
      {translate('assignDate')} : {assignDate.isValid() ? assignDate.format('DD MMM YYYY') : ''}
      {assignDate.isValid() && endDate.isValid() ? ' | ' : ''}
      {endDate.isValid() ? endDate.format('DD MMM YYYY') : ''}
    </Typography>
  )

  const maxAssessorScore = parseFloat(selectedAssessorScore?.full_score || 0).toFixed(2)
  const assessorScore = parseFloat(selectedAssessorScore?.sum_weight_score || 0).toFixed(2)
  let assessorScoreDisplay = `${translate(
    'assessorScore'
  )} : ${assessorScore} / ${maxAssessorScore}`

  let tableTitle = null
  let detailPanel = null
  let tableFooter = null
  let additionalPanel = null
  let tableDisplay = null

  if (['STT01'].includes(structureCode)) {
    tableTitle = (
      <Grid container alignItems={'flex-start'} direction={'column'}>
        <Grid item>
          <Typography variant={'h6'}>{structWeightDisplay}</Typography>
        </Grid>
        <Grid item>{dateDisplay}</Grid>
      </Grid>
    )
    detailPanel = rowData => (
      <DetailPanel
        openCommentModal={resultKpiId => openCommentModal(resultKpiId, structureCode)}
        rowData={rowData}
        inputChanged={updatedRowData => inputChanged(updatedRowData)}
        isInputStage={isInputStage}
      />
    )
    // additionalPanel = <ParentScore parentScores={structureData.parent_score || []} />
  } else if (['STT02'].includes(structureCode)) {
    let newAssScore = 0
    structureData.result_item.map(result => {
      result.result_competency.map(re => {
        if (+re.assessor_id === +selectedAssessorId) {
          newAssScore =
            newAssScore + (+re.weight_score * 100) / +structureData?.structure?.target_score
        }
        return re
      })
      return result
    })
    newAssScore = parseFloat(newAssScore || 0).toFixed(2)
    assessorScoreDisplay = `${translate('assessorScore')} : ${newAssScore} / ${maxAssessorScore}`
    tableTitle = (
      <Grid container>
        <Grid item container justify={'flex-start'}>
          <Grid item>{dateDisplay}</Grid>
        </Grid>
        <Grid item container justify={'space-between'} spacing={4}>
          <Grid item>{structWeightDisplay}</Grid>
          <Grid item>{assessorScoreDisplay}</Grid>
        </Grid>
      </Grid>
    )
    rowActions = [
      ...rowActions,
      {
        icon: () => <FontAwesomeIcon icon={icons.faComment} />,
        tooltip: translate('comment'),
        disabled: !selectedAssessorId,
        onClick: (e, rowData) =>
          openCommentModal(
            rowData.result_competency.find(item => +item.assessor_id === +selectedAssessorId)
              ?.result_competency_id,
            structureCode
          )
      }
    ]
  } else if (['STT03'].includes(structureCode)) {
    tableTitle = (
      <Grid container alignItems={'flex-start'} direction={'column'}>
        <Grid item>
          <Typography variant={'h6'}>{structWeightDisplay}</Typography>
        </Grid>
        <Grid item>{dateDisplay}</Grid>
      </Grid>
    )
  }

  if (structureCode === 'STT05') {
    tableTitle = (
      <Grid container justify={'space-between'} spacing={4}>
        <Grid item>{structWeightDisplay}</Grid>
        <Grid item>{assessorScoreDisplay}</Grid>
      </Grid>
    )
    additionalPanel = (
      <Grid container>
        <Grid item container justify={'space-between'} alignItems={'center'}>
          <Grid item>{tableTitle}</Grid>
          <Grid item>
            <IconButton onClick={calculateButtonClicked} component={'span'}>
              <FontAwesomeIcon icon={icons.faCalculator} />
            </IconButton>
          </Grid>
        </Grid>
        <Grid
          item
          container
          justify={'flex-start'}
          alignItems={'center'}
          style={{ paddingBottom: 8 }}>
          <Grid item>{dateDisplay}</Grid>
        </Grid>
      </Grid>
    )
    rowActions = []
    tableDisplay = Object.entries(structureData.result_item).map(group => {
      const [groupCode, groupAttrs] = group
      return (
        <Collapse
          key={groupCode}
          show={currentCollapse.includes(groupCode)}
          titleComponent={
            <Grid container justify={'space-between'}>
              <Grid item>{groupAttrs.competency_type_name}</Grid>
              <Grid item>{groupAttrs.weight_percent}</Grid>
            </Grid>
          }
          toggleBarClicked={() => collapseChangeHandler(groupCode)}>
          <MTable
            structure={tableStructures[structureCode]}
            detailPanel={detailPanel}
            recordData={groupAttrs.items}
            rowActions={rowActions}
            options={{ actionsColumnIndex: -1 }}
            search={false}
            paginationDisabled
            sortingDisabled
          />
        </Collapse>
      )
    })
  } else {
    tableDisplay = (
      <MTable
        title={tableTitle}
        structure={tableStructures[structureCode]}
        detailPanel={detailPanel}
        recordData={[...structureData.result_item]}
        rowActions={rowActions}
        options={{
          actionsColumnIndex: -1,
          rowStyle: rowData => {
            const isResponsible = rowData.isResponsible
            const responsibleStyle = {
              color: isResponsible && isCorporate && structureCode === 'STT01' ? '#FFF' : '#000',
              backgroundColor:
                isResponsible && isCorporate && structureCode === 'STT01' ? '#2AAEFF' : '#FFF',
              verticalAlign: 'top'
            }
            return responsibleStyle
          }
        }}
        search={false}
        paginationDisabled
        sortingDisabled
      />
    )
  }

  return (
    <Fragment>
      <PerformanceLevelModal
        modalOpen={modalOpen}
        closeModalHandler={closeModalHandler}
        translate={translate}
        targetRow={targetRow}
        setTargetRow={setTargetRow}
        inputChanged={structureCode === 'STT05' ? groupInputChanged : inputChanged}
      />
      <ActionPlansModal
        modalOpen={modalActionOpen}
        closeModalActionHandler={closeModalActionHandler}
        appraisalInfo={appraisalInfo}
        resultItemId={resultItemId}
        resultId={appraisalInfo?.result_id}
      />
      {additionalPanel}
      {tableDisplay}
      {tableFooter}
    </Fragment>
  )
}

const StyledChip = withStyles(theme => ({
  labelSmall: {
    paddingLeft: 6,
    paddingRight: 0
  }
}))(Chip)

export default withTranslate(StructureTable)
